import React from 'react';
import { AlignIcon, ALIGN_LEFT, ALIGN_CENTER, ALIGN_RIGHT, ALIGN_JUSTIFY } from './AlignIcons';
import ColorPicker from './ColorPicker';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';

const ToolbarButton = ({ active, onClick, title, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    style={{
      padding: '4px 8px', border: 'none', borderRadius: 4, cursor: 'pointer',
      background: active ? 'var(--primary)' : 'transparent',
      color: active ? '#fff' : 'var(--text-secondary)',
      fontSize: 13, fontWeight: 600, lineHeight: 1,
      transition: 'all 0.15s', minWidth: 28, display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center'
    }}
  >{children}</button>
);

const Divider = () => (
  <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 4px' }} />
);


export default function RichTextEditor({ content, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bold: false,
        italic: false,
      }),
      Bold.configure({ HTMLAttributes: {} }).extend({ addInputRules() { return []; } }),
      Italic.configure({ HTMLAttributes: {} }).extend({ addInputRules() { return []; } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 2, padding: '6px 8px',
        borderBottom: '1px solid var(--border)', background: 'var(--bg-input)',
        alignItems: 'center'
      }}>
        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">B</ToolbarButton>
        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><em>I</em></ToolbarButton>
        <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><u>U</u></ToolbarButton>
        <ToolbarButton active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><s>S</s></ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">H1</ToolbarButton>
        <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">H2</ToolbarButton>
        <ToolbarButton active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">H3</ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">•</ToolbarButton>
        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">1.</ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align left"><AlignIcon lines={ALIGN_LEFT} /></ToolbarButton>
        <ToolbarButton active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align centre"><AlignIcon lines={ALIGN_CENTER} /></ToolbarButton>
        <ToolbarButton active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align right"><AlignIcon lines={ALIGN_RIGHT} /></ToolbarButton>
        <ToolbarButton active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justify"><AlignIcon lines={ALIGN_JUSTIFY} /></ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote">"</ToolbarButton>
        {/* preserveFocus: TipTap re-focuses on command, but the mousedown
            must not blur the editor first or the selection the colour applies
            to is gone by the time the command runs. */}
        <ColorPicker
          label="Text Color"
          size={24}
          preserveFocus
          value={editor.getAttributes('textStyle').color || null}
          onChange={c => (c
            ? editor.chain().focus().setColor(c).run()
            : editor.chain().focus().unsetColor().run())}
        />
      </div>
      {/* Editor */}
      <div style={{ padding: 12, minHeight: 120, background: 'var(--bg-card)' }}>
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .ProseMirror { outline: none; min-height: 100px; color: var(--text); font-size: 14px; line-height: 1.6; }
        .ProseMirror h1 { font-size: 24px; font-weight: 700; margin: 12px 0 8px; }
        .ProseMirror h2 { font-size: 20px; font-weight: 600; margin: 10px 0 6px; }
        .ProseMirror h3 { font-size: 16px; font-weight: 600; margin: 8px 0 4px; }
        .ProseMirror p { margin: 4px 0; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 24px; margin: 4px 0; }
        .ProseMirror blockquote { border-left: 3px solid var(--border); padding-left: 12px; margin: 8px 0; color: var(--text-secondary); }
        .ProseMirror p.is-editor-empty:first-child::before { content: 'Start typing...'; color: var(--text-secondary); opacity: 0.5; pointer-events: none; float: left; height: 0; }
      `}</style>
    </div>
  );
}
